// 3.14 — original vanilla interaction system, inspired by Gooey / Orbs / Metal.
// No React, WebGL, dependency, telemetry or perpetual animation loop.
import { createCelestialCursor } from './master-xava-cursor.js?v=3.14.0';

const controls = '.mx-button,.mx-control,.mx-back,.mx-contact-link,.mx-case__close,.mx-about-station__close,.mx-contact-station__close,.mx-contact-page__back,.mx-contact-form__send,.mx-about-links a,.mx-editorial-button,.mx-pack-tools button,.mx-view-expand,.mx-motion-play,.mx-case-screens button,.mx-booking__button,.mx-booking-dialog button,.mx-booking-dialog footer a';
/*! Lucide icons, ISC / MIT. Copyright Lucide contributors and Cole Bemis. License: master-xava-lucide-license.txt */
const buttonIcons = {
  calendar: '<path d="M8 2v3M16 2v3"/><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01M16 17h.01"/>',
  send: '<path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"/><path d="m21.854 2.147-10.94 10.939"/>',
  '↗': '<path d="M7 7h10v10M7 17 17 7"/>',
  '←': '<path d="m12 19-7-7 7-7M19 12H5"/>',
  '→': '<path d="M5 12h14m-7-7 7 7-7 7"/>',
  '×': '<path d="M18 6 6 18m0-12 12 12"/>'
};

export function decorateButtonIcons(el, doc = document) {
  const walker = doc.createTreeWalker(el, 4), replacements = [];
  while (walker.nextNode()) {
    const node = walker.currentNode, match = node.textContent.match(/^(.*?)([↗←→×])\s*$/s);
    if (match && !node.parentElement.closest('svg,.mx-liquid-surface')) replacements.push([node, match[2], match[1]]);
  }
  for (const [node, glyph, prefix] of replacements) {
    const name = el.matches('.mx-booking__button') ? 'calendar' : el.matches('.mx-contact-form__send') ? 'send' : glyph;
    const icon = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    for (const [key,value] of Object.entries({viewBox:'0 0 24 24',width:'20',height:'20',fill:'none',stroke:'currentColor','stroke-width':'2','stroke-linecap':'round','stroke-linejoin':'round','aria-hidden':'true',focusable:'false','data-icon':name})) icon.setAttribute(key,value);
    icon.classList.add('mx-ui-icon');
    icon.innerHTML = buttonIcons[name];
    node.replaceWith(doc.createTextNode(prefix), icon);
  }
}
const clamp = (n,lo,hi) => Math.max(lo,Math.min(hi,n));
const closing = new WeakMap();

export function liquidPose(x,y,width,height) {
  const nx=clamp((x/Math.max(1,width)-.5)*2,-1,1),ny=clamp((y/Math.max(1,height)-.5)*2,-1,1);
  return {x:nx*3.5,y:ny*2.5,beadX:clamp(x,7,width-7),beadY:clamp(y,5,height-5),angle:nx*55+ny*30};
}

export function closeLiquidDialog(dialog,complete) {
  if(!dialog?.open || closing.has(dialog))return;
  if(matchMedia('(prefers-reduced-motion: reduce)').matches || !dialog.animate){complete();return;}
  dialog.classList.add('mx-liquid-closing');
  const animation=dialog.animate([{opacity:1,transform:'translateY(0)'},{opacity:0,transform:'translateY(18px)'}],{duration:170,easing:'cubic-bezier(.4,0,1,1)',fill:'forwards'});
  closing.set(dialog,animation);
  const finish=()=>{closing.delete(dialog);dialog.classList.remove('mx-liquid-closing');if(dialog.open)complete();animation.cancel();};
  animation.finished.then(finish,finish);
}

export function mountLiquidUI(doc=document) {
  if(doc.documentElement.dataset.mxLiquid==='true')return;
  doc.documentElement.dataset.mxLiquid='true';
  const fine=matchMedia('(hover: hover) and (pointer: fine)'),reduced=matchMedia('(prefers-reduced-motion: reduce)');
  const disposers=[],decorated=new Set(),pulses=new Map();
  let active=null,rect=null,eventPoint=null,frame=0,until=0,cursor=null,host=null,pressed=null,disposed=false;
  const on=(target,type,fn,options)=>{target.addEventListener(type,fn,options);disposers.push(()=>target.removeEventListener(type,fn,options));};
  const defs=doc.createElementNS('http://www.w3.org/2000/svg','svg');
  defs.setAttribute('aria-hidden','true');defs.setAttribute('width','0');defs.setAttribute('height','0');defs.classList.add('mx-liquid-defs');
  defs.innerHTML='<defs><filter id="mx-liquid-merge" x="-15%" y="-45%" width="130%" height="190%" color-interpolation-filters="sRGB"><feGaussianBlur in="SourceGraphic" stdDeviation="3"/><feColorMatrix type="matrix" values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 18 -7"/></filter></defs>';
  doc.body.append(defs);
  function enhance(el){
    if(!el)return null;
    decorateButtonIcons(el, doc);
    if(el.matches(':disabled,[aria-disabled="true"]'))return null;
    let surface=el.querySelector(':scope > .mx-liquid-surface');
    if(!surface){
      surface=doc.createElement('span');surface.className='mx-liquid-surface';surface.setAttribute('aria-hidden','true');
      surface.innerHTML='<i class="mx-liquid-pool"><i></i><b></b></i><i class="mx-liquid-rim"></i><i class="mx-liquid-glint"></i>';
      // Preserve a positioned/fixed control's existing coordinate system.
      if(getComputedStyle(el).position==='static')el.classList.add('mx-liquid-relative');
      el.prepend(surface);el.classList.add('mx-liquid-control');decorated.add(el);
    }
    return surface;
  }
  function resetActive(){
    if(active){active.classList.remove('is-liquid-hot');for(const key of ['--liquid-x','--liquid-y','--liquid-angle'])active.style.removeProperty(key);}
    active=null;rect=null;eventPoint=null;
  }
  function setActive(el){
    if(el===active)return;
    resetActive();if(!el || !enhance(el))return;
    active=el;rect=el.getBoundingClientRect();el.classList.add('is-liquid-hot');
  }
  function hideCursor(){cursor?.leave();doc.body.classList.remove('has-liquid-tracker');}
  function track(e){
    // The existing universe clock owns its cursor. This clock only wakes in
    // dialogs/contact/editorial pages where the universe is paused or absent.
    const dialog=e.target.closest('dialog[open]');
    const outside=!e.target.closest('[data-master-xava]') || dialog;
    if(!outside || e.target.closest('input,textarea,select,[contenteditable],iframe,video') || e.pointerType==='touch' || !fine.matches || reduced.matches){hideCursor();return;}
    if(!host){host=doc.createElement('div');host.className='mx-liquid-tracker-host';host.setAttribute('aria-hidden','true');cursor=createCelestialCursor(host,{allowDialogs:true});}
    const parent=dialog||doc.body;if(host.parentNode!==parent)parent.append(host);
    cursor.move(e);doc.body.classList.add('has-liquid-tracker');until=performance.now()+340;
  }
  function render(now){
    frame=0;if(disposed || doc.hidden || reduced.matches)return;
    if(active && eventPoint && rect){
      const p=liquidPose(eventPoint.x-rect.left,eventPoint.y-rect.top,rect.width,rect.height);
      active.style.setProperty('--liquid-x',p.x.toFixed(2)+'px');active.style.setProperty('--liquid-y',p.y.toFixed(2)+'px');
      active.style.setProperty('--liquid-bx',p.beadX.toFixed(1)+'px');active.style.setProperty('--liquid-by',p.beadY.toFixed(1)+'px');
      active.style.setProperty('--liquid-angle',p.angle.toFixed(1)+'deg');eventPoint=null;
    }
    cursor?.render({now,reduced:reduced.matches});
    if(now<until && doc.body.classList.contains('has-liquid-tracker'))frame=requestAnimationFrame(render);
  }
  function wake(){if(!frame&&!disposed&&!doc.hidden&&!reduced.matches)frame=requestAnimationFrame(render);}
  function pulse(el){
    const surface=enhance(el);if(!surface||reduced.matches||!surface.animate)return;
    pulses.get(el)?.cancel();
    const animation=surface.animate([{transform:'scale(.965,.94)'},{transform:'scale(1.025,1.055)',offset:.48},{transform:'scale(.995,.99)',offset:.75},{transform:'scale(1)'}],{duration:440,easing:'cubic-bezier(.22,.68,.25,1)'});
    pulses.set(el,animation);const done=()=>{if(pulses.get(el)===animation)pulses.delete(el);};animation.finished.then(done,done);
  }
  const targetOf=e=>e.target instanceof Element?e.target:null;
  on(doc,'pointermove',e=>{
    if(!targetOf(e))return;
    if(e.pointerType==='touch'||!fine.matches||reduced.matches){resetActive();hideCursor();return;}
    setActive(e.target.closest(controls));if(active)eventPoint={x:e.clientX,y:e.clientY};track(e);wake();
  },{passive:true});
  on(doc,'pointerover',e=>{if(targetOf(e)&&e.pointerType!=='touch'&&fine.matches&&!reduced.matches){setActive(e.target.closest(controls));wake();}},{passive:true});
  on(doc,'pointerdown',e=>{
    if(!targetOf(e)||e.button!==0)return;
    pressed=e.target.closest(controls);enhance(pressed);pressed?.classList.add('is-liquid-pressed');
    const drag=e.target.closest('.mx-pack-scene canvas');doc.body.classList.toggle('mx-liquid-dragging',Boolean(drag));
    doc.body.classList.add('mx-liquid-pointer-down');
  },{passive:true});
  function release(){pressed?.classList.remove('is-liquid-pressed');pressed=null;doc.body.classList.remove('mx-liquid-pointer-down','mx-liquid-dragging');}
  on(doc,'pointerup',release,{passive:true});on(doc,'pointercancel',release,{passive:true});on(doc,'lostpointercapture',release,{passive:true});
  on(doc,'click',e=>{const el=targetOf(e)?.closest(controls);if(el)pulse(el);},{passive:true});
  on(doc,'focusin',e=>{const el=targetOf(e)?.closest(controls);if(el)enhance(el);});
  on(doc,'pointerout',e=>{if(!e.relatedTarget||e.relatedTarget.tagName==='IFRAME'){hideCursor();resetActive();}});
  function stop(){cancelAnimationFrame(frame);frame=0;until=0;hideCursor();resetActive();release();for(const animation of pulses.values())animation.cancel();pulses.clear();}
  on(window,'blur',stop);on(doc,'visibilitychange',()=>{if(doc.hidden)stop();});
  on(doc,'scroll',()=>{rect=active?.getBoundingClientRect()||null;hideCursor();},{passive:true,capture:true});
  on(window,'resize',()=>{resetActive();hideCursor();},{passive:true});
  on(reduced,'change',stop);on(fine,'change',stop);
  // Only watch the open attribute: no gallery subtree scan or observer loop.
  const observer=new MutationObserver(records=>{for(const {target} of records)if(target.tagName==='DIALOG'){stop();target.querySelectorAll(controls).forEach(enhance);}});
  observer.observe(doc.body,{attributes:true,subtree:true,attributeFilter:['open']});
  doc.querySelectorAll(controls).forEach(enhance);
  function destroy(){
    if(disposed)return;disposed=true;stop();observer.disconnect();disposers.splice(0).forEach(fn=>fn());cursor?.destroy();host?.remove();defs.remove();
    for(const el of decorated){el.classList.remove('mx-liquid-control','mx-liquid-relative');el.querySelector(':scope > .mx-liquid-surface')?.remove();}decorated.clear();delete doc.documentElement.dataset.mxLiquid;
  }
  on(window,'pagehide',e=>{if(e.persisted)stop();else destroy();});
  return {destroy};
}
if(typeof document!=='undefined')mountLiquidUI();
